/**
 * <promo-popup> — newsletter popup with a configurable trigger (time delay, scroll
 * depth or exit intent), dismissed state remembered in localStorage with a TTL.
 * A trigger that fires while the cart drawer is open waits for the drawer to close
 * instead: the drawer sits at z-index 1000 and the popup at 1010, so the popup used
 * to cover the drawer's contents - the drawer was unusable on a phone.
 * Depends on the global `trapFocus` / `removeTrapFocus` helpers from global.js,
 * loaded theme-wide before this deferred script.
 */
class PromoPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.promo-popup__dialog');
    this.storageKey = this.dataset.storageKey;
    this.sessionKey = `${this.dataset.storageKey}-session`;
    this.rememberDays = Number(this.dataset.rememberDays) || 14;
    this.trigger = this.dataset.trigger;
    this.delaySeconds = Number(this.dataset.delaySeconds) || 0;
    this.scrollPercent = Number(this.dataset.scrollPercent) || 40;
    this.forceOpen = this.dataset.forceOpen === 'true';

    this.isOpen = false;
    this.previouslyFocusedElement = null;
    this.delayTimeoutId = null;
    this.scrollRafId = null;
    this.pendingOpen = false;
    this.cartObserver = null;

    this.onDismissClick = this.onDismissClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onExitIntent = this.onExitIntent.bind(this);
    this.onEditorSelect = this.onEditorSelect.bind(this);
    this.onEditorDeselect = this.onEditorDeselect.bind(this);
    this.onTriggerReady = this.onTriggerReady.bind(this);
    this.onCartMutation = this.onCartMutation.bind(this);

    this.querySelectorAll('[data-promo-dismiss]').forEach((element) => {
      element.addEventListener('click', this.onDismissClick);
    });

    // Shopify answers a signup with a redirect, so the success state arrives as a
    // fresh page load. Reopen the popup then — even for a visitor who dismissed
    // it earlier — otherwise the discount code is never shown to the person who
    // just subscribed to get it.
    this.hasSucceeded = this.querySelector('[data-promo-success]') !== null;

    if (this.hasSucceeded) {
      this.open();
      return;
    }

    // In the theme editor the popup opens only while its section is selected.
    // Opening it on every re-render - which is what a `force open` attribute
    // does, since every settings change re-renders the section - made the
    // editor unusable: the popup came back a second after each edit.
    if (this.forceOpen) {
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
      return;
    }

    if (this.isDismissed() || this.wasShownThisSession()) return;

    this.setupTrigger();
  }

  disconnectedCallback() {
    this.teardownTrigger();
    this.stopObservingCartDrawer();
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('shopify:section:select', this.onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
  }

  onEditorSelect(event) {
    if (event.target.contains(this)) this.open();
  }

  onEditorDeselect(event) {
    if (event.target.contains(this)) this.close();
  }

  setupTrigger() {
    if (this.trigger === 'scroll') {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    } else if (this.trigger === 'exit') {
      document.addEventListener('mouseleave', this.onExitIntent);
    } else {
      this.delayTimeoutId = window.setTimeout(this.onTriggerReady, this.delaySeconds * 1000);
    }
  }

  teardownTrigger() {
    window.clearTimeout(this.delayTimeoutId);
    window.cancelAnimationFrame(this.scrollRafId);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('mouseleave', this.onExitIntent);
  }

  onScroll() {
    // Throttle with rAF instead of reacting to every scroll event.
    if (this.scrollRafId) return;
    this.scrollRafId = window.requestAnimationFrame(() => {
      this.scrollRafId = null;
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;
      const scrolledPercent = (window.scrollY / scrollableHeight) * 100;
      if (scrolledPercent >= this.scrollPercent) {
        this.teardownTrigger();
        this.onTriggerReady();
      }
    });
  }

  onExitIntent(event) {
    // Fires when the cursor actually leaves the viewport through the top edge.
    if (event.clientY > 0) return;
    this.teardownTrigger();
    this.onTriggerReady();
  }

  onDismissClick(event) {
    event.preventDefault();
    this.close();
  }

  onKeyDown(event) {
    if (event.code === 'Escape' || event.key === 'Escape') this.close();
  }

  /*
   * A trigger fired. The drawer covers the page and this popup sits above it, so
   * opening now would hide the drawer's contents. Wait for the drawer to close
   * instead: it marks itself with `active`, and it also toggles `overflow-hidden`
   * on the body for its own scroll lock. Both are watched, because the drawer's
   * own class is the precise signal while the body class survives the drawer
   * element being re-rendered. A drawer already open when the trigger fires is
   * caught by the check; one opened afterwards is caught by the observer.
   */
  onTriggerReady() {
    if (this.cartDrawerIsOpen()) {
      this.pendingOpen = true;
      this.observeCartDrawer();
      return;
    }

    this.open();
  }

  cartDrawerIsOpen() {
    const drawer = document.querySelector('cart-drawer');
    return Boolean(drawer && drawer.classList.contains('active'));
  }

  observeCartDrawer() {
    if (this.cartObserver) return;

    this.cartObserver = new MutationObserver(this.onCartMutation);
    this.cartObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      this.cartObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  onCartMutation() {
    if (!this.pendingOpen || this.cartDrawerIsOpen()) return;
    this.pendingOpen = false;
    this.stopObservingCartDrawer();
    this.open();
  }

  stopObservingCartDrawer() {
    this.cartObserver?.disconnect();
    this.cartObserver = null;
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.markShownThisSession();
    this.previouslyFocusedElement = document.activeElement;

    this.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeyDown);

    const elementToFocus = this.dialog.querySelector('input, button, [href]') || this.dialog;
    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, elementToFocus);
    } else {
      elementToFocus.focus();
    }
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeyDown);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.previouslyFocusedElement);
    } else if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }

    this.persistDismissal();
  }

  /*
   * Dismissal is remembered for `rememberDays`, but a visitor who never closes
   * the popup - who just keeps browsing - used to meet it again on every single
   * page. Once per session is enough; the long dismissal still applies on top.
   */
  wasShownThisSession() {
    try {
      return window.sessionStorage.getItem(this.sessionKey) === '1';
    } catch (error) {
      return false;
    }
  }

  markShownThisSession() {
    try {
      window.sessionStorage.setItem(this.sessionKey, '1');
    } catch (error) {
      // Storage unavailable: fall back to the per-page-load behaviour.
    }
  }

  isDismissed() {
    if (!this.storageKey) return false;
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return false;
      const { dismissedAt } = JSON.parse(raw);
      const elapsedDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      return elapsedDays < this.rememberDays;
    } catch (error) {
      // Storage unavailable (private browsing, blocked cookies, ...): never block the popup.
      return false;
    }
  }

  persistDismissal() {
    if (!this.storageKey) return;
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ dismissedAt: Date.now() }));
    } catch (error) {
      // Storage unavailable: the popup will simply be shown again on the next page load.
    }
  }
}

customElements.define('promo-popup', PromoPopup);
